import { URL_PATHS } from '@constants'
import { useStore } from '@hooks/useStore'
import { Box, CircularProgress } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { POLICIES } from 'src/constants/policies.constants'

export interface IPolicyHocProps {
  children: JSX.Element
}

export const OvPoliciesHoc = observer(({ children }: IPolicyHocProps) => {
  const { company, policies } = useStore()
  const location = useLocation()
  const [isLoading, setIsLoading] = useState(true)
  const [hasValidCompany, setHasValidCompany] = useState(false)

  const hasCompanyPolicies = [
    POLICIES.CLIENT.READ,
    POLICIES.CLIENT.CREATE,
    POLICIES.CLIENT.UPDATE,
    POLICIES.CLIENT.DELETE,
    POLICIES.DOCUMENT.CREATE,
    POLICIES.DOCUMENT.READ,
    POLICIES.DOCUMENT.UPDATE,
    POLICIES.DOCUMENT.DELETE,
    POLICIES.COMPANY.DELETE,
    POLICIES.COMPANY.READ,
    POLICIES.COMPANY.UPDATE,
  ]

  const hasNoCompanyPolicies = [POLICIES.COMPANY.CREATE]

  // SET COMPANY POLICIES
  useEffect(() => {
    const checkCompany = async () => {
      try {
        // Always fetch from server to ensure we have the latest data
        const companyData = await company.getMyCompany()

        // Check if company exists and has required data
        const hasCompany = company.has_company && company.current_company?.name && company.current_company?.id

        setHasValidCompany(!!hasCompany)

        if (hasCompany) {
          policies.addPolicies(hasCompanyPolicies)
          policies.removePolicies(hasNoCompanyPolicies)
        } else {
          policies.removePolicies(hasCompanyPolicies)
          policies.addPolicies(hasNoCompanyPolicies)
        }
      } catch (error) {
        console.error('[OvPoliciesHoc] Error checking company:', error)
        setHasValidCompany(false)
        policies.removePolicies(hasCompanyPolicies)
        policies.addPolicies(hasNoCompanyPolicies)
      } finally {
        setIsLoading(false)
      }
    }

    checkCompany()
  }, [location.pathname])

  useEffect(() => {
    return () => policies.reset()
  }, [])

  // Show loading state while checking company
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh">
        <CircularProgress />
      </Box>
    )
  }

  // Redirect to onboarding if no valid company
  if (!hasValidCompany) {
    return (
      <Navigate
        to={URL_PATHS.WELCOME}
        replace
      />
    )
  }

  return <>{children}</>
})
